import UserRole from './user-role';
import { FeatureType } from '../hooks/features';

/**
 * Permission types that can be checked in navigation guards
 */
export enum Permission {
  // Role-based permissions
  VIEW_DASHBOARD = 'VIEW_DASHBOARD',
  VIEW_VETERINARY_DASHBOARD = 'VIEW_VETERINARY_DASHBOARD',
  VIEW_ANIMAL_SEARCH = 'VIEW_ANIMAL_SEARCH',
  VIEW_ANIMAL_COMPARE = 'VIEW_ANIMAL_COMPARE',
  VIEW_ANIMAL_REPORTS = 'VIEW_ANIMAL_REPORTS',
  VIEW_LOCATIONS = 'VIEW_LOCATIONS',
  VIEW_LOCATION_CLIMATE = 'VIEW_LOCATION_CLIMATE',
  VIEW_FEED_SILOS = 'VIEW_FEED_SILOS',
  VIEW_TASKS = 'VIEW_TASKS',
  VIEW_MEDICAL_INFO = 'VIEW_MEDICAL_INFO',
  VIEW_MEDICATION_CATALOGUE = 'VIEW_MEDICATION_CATALOGUE',
  VIEW_EMISSION_SOURCES = 'VIEW_EMISSION_SOURCES',
  VIEW_SLAUGHTER_PREPARATION = 'VIEW_SLAUGHTER_PREPARATION',
  VIEW_ANALYTICS = 'VIEW_ANALYTICS',

  // Farm settings
  VIEW_FARM_SETTINGS = 'VIEW_FARM_SETTINGS',
  VIEW_SYSTEM_SETTINGS = 'VIEW_SYSTEM_SETTINGS',
  VIEW_SYSTEM_DATA = 'VIEW_SYSTEM_DATA',
  VIEW_FARM_INFO = 'VIEW_FARM_INFO',
  VIEW_FARM_DOCUMENTS = 'VIEW_FARM_DOCUMENTS',
  VIEW_DEVICES = 'VIEW_DEVICES',
  VIEW_SENSOR_DEVICES = 'VIEW_SENSOR_DEVICES',

  // Veterinary-specific permissions
  VIEW_VET_LOCATIONS = 'VIEW_VET_LOCATIONS',
  VIEW_VET_ORDERS = 'VIEW_VET_ORDERS',
  VIEW_VET_MEDICAL_SUPPLIES = 'VIEW_VET_MEDICAL_SUPPLIES',
  VIEW_VET_BOTTLES = 'VIEW_VET_BOTTLES',
  VIEW_VET_LABELS = 'VIEW_VET_LABELS',
  VIEW_VET_VACCINATION_RULES = 'VIEW_VET_VACCINATION_RULES',

  // Admin permissions
  VIEW_USER_MANAGEMENT = 'VIEW_USER_MANAGEMENT',
  VIEW_SLAUGHTERHOUSE_MANAGEMENT = 'VIEW_SLAUGHTERHOUSE_MANAGEMENT',
  VIEW_FARMS_OVERVIEW = 'VIEW_FARMS_OVERVIEW',

  // Operational strategy
  VIEW_OPERATIONAL_STRATEGY = 'VIEW_OPERATIONAL_STRATEGY'
}

/**
 * Configuration object that maps permissions to their requirements
 */
export interface PermissionConfig {
  roles?: UserRole[];
  features?: FeatureType[];
  requireAll?: boolean; // If true, user must have ALL roles. If false, user needs ANY role
}

/**
 * Permission configuration map
 * Defines which roles and features are required for each permission
 */
export const PERMISSION_CONFIG: Record<Permission, PermissionConfig> = {
  // Dashboard permissions
  [Permission.VIEW_DASHBOARD]: {
    roles: [UserRole.user, UserRole.admin, UserRole.manager]
  },
  [Permission.VIEW_VETERINARY_DASHBOARD]: {
    roles: [UserRole.veterinary]
  },

  // Animal permissions
  [Permission.VIEW_ANIMAL_SEARCH]: {
    roles: [UserRole.user, UserRole.admin, UserRole.manager, UserRole.veterinary]
  },
  [Permission.VIEW_ANIMAL_COMPARE]: {
    roles: [UserRole.user, UserRole.admin, UserRole.manager, UserRole.veterinary],
    features: [FeatureType.USE_COMPARE_FUNCTIONALITY]
  },
  [Permission.VIEW_ANIMAL_REPORTS]: {
    roles: [UserRole.user, UserRole.admin, UserRole.manager, UserRole.veterinary],
    features: [FeatureType.USE_COMPARE_FUNCTIONALITY]
  },

  // Location permissions
  [Permission.VIEW_LOCATIONS]: {
    roles: [UserRole.user, UserRole.admin, UserRole.manager, UserRole.veterinary]
  },
  [Permission.VIEW_LOCATION_CLIMATE]: {
    roles: [UserRole.user, UserRole.admin, UserRole.manager, UserRole.veterinary]
  },
  [Permission.VIEW_FEED_SILOS]: {
    roles: [UserRole.admin, UserRole.manager],
    features: [FeatureType.VIEW_SILO]
  },

  // Task permissions
  [Permission.VIEW_TASKS]: {
    roles: [UserRole.user, UserRole.admin, UserRole.manager]
  },

  // Medical information
  [Permission.VIEW_MEDICAL_INFO]: {
    roles: [UserRole.user, UserRole.admin, UserRole.manager]
  },

  // The sourced medication catalogue. Behind a feature flag while it is in beta, and open to the
  // veterinary role only - loading it is an admin action run by the import tool, not something a
  // veterinarian does, so the two sit on different endpoints.
  [Permission.VIEW_MEDICATION_CATALOGUE]: {
    roles: [UserRole.veterinary],
    features: [FeatureType.USE_MEDICATION_CATALOGUE]
  },

  // Emission sources
  [Permission.VIEW_EMISSION_SOURCES]: {
    roles: [UserRole.admin, UserRole.manager]
  },

  // Slaughter preparation
  [Permission.VIEW_SLAUGHTER_PREPARATION]: {
    roles: [UserRole.admin, UserRole.manager],
    features: [FeatureType.USE_SLAUGHTER_PREPARATION_ANIMALS]
  },

  // Analytics
  [Permission.VIEW_ANALYTICS]: {
    roles: [UserRole.user, UserRole.admin, UserRole.manager, UserRole.veterinary]
  },

  // Settings
  [Permission.VIEW_FARM_SETTINGS]: {
    roles: [UserRole.admin, UserRole.manager]
  },
  [Permission.VIEW_SYSTEM_SETTINGS]: {
    roles: [UserRole.admin]
  },
  [Permission.VIEW_SYSTEM_DATA]: {
    roles: [UserRole.admin]
  },
  [Permission.VIEW_FARM_INFO]: {
    roles: [UserRole.admin, UserRole.manager]
  },
  // Deliberately separate from VIEW_FARM_INFO, which guards all four Farm Info tabs at once: an export
  // notification reaches every role, so a user following one has to reach the documents tab - and only that
  // tab. Widening VIEW_FARM_INFO would hand them Animal Capacity, Carbon Footprint and Feed Conversion too.
  [Permission.VIEW_FARM_DOCUMENTS]: {
    roles: [UserRole.admin, UserRole.manager, UserRole.user]
  },
  [Permission.VIEW_DEVICES]: {
    roles: [UserRole.admin, UserRole.manager]
  },
  // Binding a sensor device to a pen decides which pen a climate history is attributed to, so it stays with
  // the roles that manage the farm - deliberately not VIEW_LOCATION_CLIMATE, which is open to all four
  // including veterinary because reading a chart is not the same as deciding whose chart it is. The backend
  // guards all four sensor-device endpoints as Admin + Manager, so this matches it rather than widening it.
  [Permission.VIEW_SENSOR_DEVICES]: {
    roles: [UserRole.admin, UserRole.manager]
  },

  // Veterinary-specific permissions
  [Permission.VIEW_VET_LOCATIONS]: {
    roles: [UserRole.veterinary]
  },
  [Permission.VIEW_VET_ORDERS]: {
    roles: [UserRole.veterinary]
  },
  [Permission.VIEW_VET_MEDICAL_SUPPLIES]: {
    roles: [UserRole.veterinary]
  },
  [Permission.VIEW_VET_BOTTLES]: {
    roles: [UserRole.veterinary]
  },
  [Permission.VIEW_VET_LABELS]: {
    roles: [UserRole.veterinary]
  },
  [Permission.VIEW_VET_VACCINATION_RULES]: {
    roles: [UserRole.veterinary]
  },

  // Admin permissions
  [Permission.VIEW_USER_MANAGEMENT]: {
    roles: [UserRole.admin]
  },
  [Permission.VIEW_SLAUGHTERHOUSE_MANAGEMENT]: {
    roles: [UserRole.admin],
    features: [FeatureType.USE_SLAUGHTERHOUSE_MANAGEMENT]
  },
  // The overview reads across every farm on the platform, so it stays with the roles that already see
  // system-wide data. It also guards /dashboard/:farmId, which is the same data one farm at a time.
  [Permission.VIEW_FARMS_OVERVIEW]: {
    roles: [UserRole.admin]
  },

  // Operational strategy
  [Permission.VIEW_OPERATIONAL_STRATEGY]: {
    roles: [UserRole.user, UserRole.admin, UserRole.manager]
  }
};
